<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { z } from "zod"
import { LIST_CO_HOSTS_KEY, useInviteCoHost } from "@/api"
import { Button, Drawer, Input } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { AddCoHostDrawerKey } from "@/symbols"

interface AddCoHostDrawerProps {
  eventVaultId?: number
}

const { eventVaultId } = defineProps<AddCoHostDrawerProps>()

const emit = defineEmits<{
  (e: "success", message: string): void
  (e: "error", message: string): void
}>()

const { t } = useTranslation()

const { toggle } = useModal(AddCoHostDrawerKey)

const validationSchema = toTypedSchema(
  z.object({
    name: z.string().max(250),
    email: z.string().email().max(250),
  }),
)

const { handleSubmit, handleReset, values } = useForm({ validationSchema })

const { mutate: mutateInviteUser, isPending } = useInviteCoHost(eventVaultId)

const client = useQueryClient()

const onSubmitInvite = handleSubmit(() => {
  mutateInviteUser({ email: values.email ?? "", name: values.name ?? "" }, {
    onSuccess: async () => {
      handleReset()
      await client.invalidateQueries({ queryKey: [LIST_CO_HOSTS_KEY, eventVaultId] })
      toggle()
      emit("success", t("add_co_host_drawer.success"))
    },
    onError: () => emit("error", t("add_co_host_drawer.error")),
  })
})
</script>

<template>
  <Drawer :name="AddCoHostDrawerKey" :title="t('add_co_host_drawer.title')">
    <form class="flex flex-col gap-2" @submit.prevent="onSubmitInvite">
      <Input name="name" :label="t('add_co_host_drawer.name_label')" :disabled="isPending" />
      <Input name="email" :label="t('add_co_host_drawer.email_label')" :disabled="isPending" />
      <Button class=" w-fit px-10" :disabled="isPending">
        {{ t("add_co_host_drawer.add_btn") }}
      </Button>
    </form>
  </Drawer>
</template>
